import { api } from "@app/api";
import { createSelector } from "@app/fx";
import { defaultEntity } from "@app/hal";
import { defaultHalHref } from "@app/hal";
import { schema } from "@app/schema";
import type { WebState } from "@app/schema";
import type { LinkResponse, OperationStatus } from "@app/types";

// Response payload from Deploy API for external AWS accounts
export interface DeployExternalAwsAccountResponse {
  id: number;
  organization_id: string;
  aws_account_id: string;
  account_name: string;
  aws_region_primary: string;
  discovery_enabled: boolean;
  discovery_frequency: string;
  last_discovery_at: string | null;
  status: string;
  created_by_user_email: string;
  created_by_user_name: string;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  _links: {
    organization: LinkResponse;
    self: LinkResponse;
    external_aws_resources?: LinkResponse;
  };
  _type: "external_aws_account";
}

// Domain model (local to this module until global types are added)
export interface DeployExternalAwsAccount {
  id: string;
  organizationId: string;
  awsAccountId: string;
  accountName: string;
  awsRegionPrimary: string;
  discoveryEnabled: boolean;
  discoveryFrequency: string;
  lastDiscoveryAt: string;
  status: string;
  createdByUserEmail: string;
  createdByUserName: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export const defaultExternalAwsAccountResponse = (
  e: Partial<DeployExternalAwsAccountResponse> = {},
): DeployExternalAwsAccountResponse => {
  const now = new Date().toISOString();
  return {
    id: 0,
    organization_id: "",
    aws_account_id: "",
    account_name: "",
    aws_region_primary: "",
    discovery_enabled: false,
    discovery_frequency: "",
    last_discovery_at: null,
    status: "unknown",
    created_by_user_email: "",
    created_by_user_name: "",
    created_by_user_id: "",
    created_at: now,
    updated_at: now,
    _links: {
      organization: defaultHalHref(),
      self: defaultHalHref(),
      external_aws_resources: defaultHalHref(),
      ...(e._links || {}),
    },
    _type: "external_aws_account",
    ...e,
  };
};

export const deserializeExternalAwsAccount = (
  payload: DeployExternalAwsAccountResponse,
): DeployExternalAwsAccount => {
  return {
    id: `${payload.id}`,
    organizationId: payload.organization_id,
    awsAccountId: payload.aws_account_id,
    accountName: payload.account_name,
    awsRegionPrimary: payload.aws_region_primary,
    discoveryEnabled: payload.discovery_enabled,
    discoveryFrequency: payload.discovery_frequency,
    lastDiscoveryAt: payload.last_discovery_at || "",
    status: payload.status,
    createdByUserEmail: payload.created_by_user_email,
    createdByUserName: payload.created_by_user_name,
    createdByUserId: payload.created_by_user_id,
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
};

// API endpoints
export const fetchExternalAwsAccountById = api.get<{ id: string }>(
  "/external_aws_accounts/:id",
);

export const fetchExternalAwsAccounts = api.get("/external_aws_accounts");

interface SyncExternalAwsAccountResponse {
  operation_id: number;
  status: OperationStatus;
}

export const syncExternalAwsAccount = api.post<
  { id: string },
  SyncExternalAwsAccountResponse
>("/external_aws_accounts/:id/sync", function* (ctx, next) {
  yield* next();

  if (!ctx.json.ok) {
    return;
  }

  ctx.loader = {
    message: "Sync started.",
    meta: {
      operationId: `${ctx.json.value.operation_id}`,
    },
  };
});

// Entity registration for HAL parser
export const externalAwsAccountEntities = {
  external_aws_account: defaultEntity({
    id: "external_aws_account",
    deserialize: deserializeExternalAwsAccount,
    save: schema.externalAwsAccounts.add,
  }),
};

// Selectors
export const selectExternalAwsAccountById =
  schema.externalAwsAccounts.selectById;
export const selectExternalAwsAccountsAsList =
  schema.externalAwsAccounts.selectTableAsList;

export const selectExternalAwsAccountsForTable = createSelector(
  selectExternalAwsAccountsAsList,
  (_: WebState, p: { search: string }) => p.search.toLowerCase(),
  (accounts, search) => {
    const filtered = search
      ? accounts.filter(
          (a) =>
            a.accountName.toLowerCase().includes(search) ||
            a.awsAccountId.toLowerCase().includes(search),
        )
      : accounts;
    return [...filtered].sort((a, b) =>
      a.accountName.localeCompare(b.accountName),
    );
  },
);

interface UpdateExternalAwsAccount {
  id: string;
  accountName: string;
}

export const updateExternalAwsAccount = api.put<UpdateExternalAwsAccount>(
  "/external_aws_accounts/:id",
  function* (ctx, next) {
    const { accountName } = ctx.payload;
    const body = {
      account_name: accountName,
    };
    ctx.request = ctx.req({ body: JSON.stringify(body) });

    yield* next();

    if (!ctx.json.ok) {
      return;
    }

    ctx.loader = {
      message: "Saved changes successfully!",
    };
  },
);
